import React, { useState } from 'react';
import { calculateMockPanchang } from '../../services/panchangApi';
import { ChevronLeft, ChevronRight, Moon, Sun } from 'lucide-react';

const WEEKDAYS = [
  { en: "Sun", hi: "रवि" },
  { en: "Mon", hi: "सोम" },
  { en: "Tue", hi: "मंगल" },
  { en: "Wed", hi: "बुध" },
  { en: "Thu", hi: "गुरु" },
  { en: "Fri", hi: "शुक्र" },
  { en: "Sat", hi: "शनि" }
];

const MONTHS_LIST = [
  { nameHi: "जनवरी (January)", nameEn: "January", index: 0 },
  { nameHi: "फरवरी (February)", nameEn: "February", index: 1 },
  { nameHi: "मार्च (March)", nameEn: "March", index: 2 },
  { nameHi: "अप्रैल (April)", nameEn: "April", index: 3 },
  { nameHi: "मई (May)", nameEn: "May", index: 4 },
  { nameHi: "जून (June)", nameEn: "June", index: 5 },
  { nameHi: "जुलाई (July)", nameEn: "July", index: 6 },
  { nameHi: "अगस्त (August)", nameEn: "August", index: 7 },
  { nameHi: "सितंबर (September)", nameEn: "September", index: 8 },
  { nameHi: "अक्टूबर (October)", nameEn: "October", index: 9 },
  { nameHi: "नवंबर (November)", nameEn: "November", index: 10 },
  { nameHi: "दिसंबर (December)", nameEn: "December", index: 11 }
];

const getShortTithiName = (tithi) => {
  const name = tithi.name.hi;
  // Parse out the tithi word from the "पक्ष - तिथि" string
  if (name.includes('-')) {
    return name.split('-')[1].trim();
  }
  return name;
};

const toHindiNumeral = (num) => {
  const hindiDigits = ['०', '१', '२', '३', '४', '५', '६', '७', '८', '९'];
  return num.toString().split('').map(d => hindiDigits[d]).join('');
};

const MonthlyGrid = ({ selectedDate, onDateChange }) => {
  const [currentMonthIdx, setCurrentMonthIdx] = useState(() => selectedDate.getMonth());

  const handleMonthShift = (direction) => {
    let nextMonth = currentMonthIdx + direction;
    if (nextMonth >= 0 && nextMonth <= 11) {
      setCurrentMonthIdx(nextMonth);
    }
  };

  const selectDay = (dayNum) => {
    onDateChange(new Date(2026, currentMonthIdx, dayNum));
  };

  // Generate grid days
  const totalDays = new Date(2026, currentMonthIdx + 1, 0).getDate();
  const firstDayOfWeek = new Date(2026, currentMonthIdx, 1).getDay(); // 0 (Sun) to 6 (Sat)
  
  const cells = [];
  
  // Prev month padding cells
  for (let i = 0; i < firstDayOfWeek; i++) {
    cells.push({ isPadding: true, key: `padding-${i}` });
  }

  // Active days cells
  for (let d = 1; d <= totalDays; d++) {
    const cellDate = new Date(2026, currentMonthIdx, d);
    const panchang = calculateMockPanchang(cellDate);
    
    const isSelected = selectedDate.getDate() === d && selectedDate.getMonth() === currentMonthIdx;
    
    cells.push({
      isPadding: false,
      key: `day-${d}`,
      dayNum: d,
      date: cellDate,
      panchang: panchang,
      isSelected: isSelected
    });
  }

  return (
    <div className="bg-white rounded-3xl border border-stone-150 p-6 shadow-sm">
      {/* Month Navigation Header */}
      <div className="flex items-center justify-between mb-6 pb-4 border-b border-stone-100">
        <h3 className="font-extrabold text-stone-900 text-lg flex items-center space-x-2">
          <span>📅 पंचांग कैलेंडर 2026 (Monthly Calendar)</span>
        </h3>
        
        <div className="flex items-center space-x-2">
          {/* Previous Month Arrow */}
          <button
            onClick={() => handleMonthShift(-1)}
            disabled={currentMonthIdx === 0}
            className="p-1.5 rounded-lg border border-stone-200 text-stone-600 hover:text-saffron-600 hover:bg-saffron-50 disabled:opacity-30 disabled:hover:bg-transparent transition-all"
            title="पिछला महीना"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          
          <span className="font-bold text-stone-850 px-4 text-sm w-36 text-center">
            {MONTHS_LIST[currentMonthIdx].nameHi} 2026
          </span>
          
          {/* Next Month Arrow */}
          <button
            onClick={() => handleMonthShift(1)}
            disabled={currentMonthIdx === 11}
            className="p-1.5 rounded-lg border border-stone-200 text-stone-600 hover:text-saffron-600 hover:bg-saffron-50 disabled:opacity-30 disabled:hover:bg-transparent transition-all"
            title="अगला महीना"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Weekday Names Header */}
      <div className="grid grid-cols-7 gap-2 mb-2 text-center">
        {WEEKDAYS.map((day, idx) => (
          <div key={idx} className="py-1 text-xs font-bold text-stone-400 uppercase tracking-wide">
            {day.hi}
          </div>
        ))}
      </div>

      {/* Days Calendar Grid */}
      <div className="grid grid-cols-7 gap-2">
        {cells.map((cell) => {
          if (cell.isPadding) {
            return (
              <div 
                key={cell.key} 
                className="min-h-[140px] bg-stone-50/40 border border-dashed border-stone-100"
              />
            );
          }

          const { dayNum, panchang, isSelected } = cell;
          const isShukla = panchang.tithi.paksha === "Shukla";
          const shortTithi = getShortTithiName(panchang.tithi);

          // Astrological Colors: Saffron-Gold for Shukla, Deep-Blue for Krishna
          const pakshaColorStyles = isShukla 
            ? "bg-[#ffffe0] hover:bg-[#fffacd] border-stone-200 text-stone-900" 
            : "bg-white hover:bg-stone-50 border-stone-200 text-stone-900";

          const selectedStyles = "ring-2 ring-saffron-500 border-saffron-400 bg-white text-saffron-950 scale-[1.02] shadow-md z-10";

          return (
            <button
              key={cell.key}
              onClick={() => selectDay(dayNum)}
              className={`min-h-[140px] border p-1.5 sm:p-2 flex flex-col justify-between transition-all duration-200 relative overflow-hidden ${isSelected ? selectedStyles : pakshaColorStyles}`}
            >
              {/* Top Row: Paksha & Tithi */}
              <div className="flex justify-between w-full">
                <span className="text-[10px] sm:text-xs font-semibold text-stone-800 text-left">
                  {isShukla ? 'शु' : 'कृ'} {panchang.tithi.number} {shortTithi === "Amavasya" ? "अमावस्या" : shortTithi === "Purnima" ? "पूर्णिमा" : shortTithi}
                </span>
              </div>

              {/* Center Row: Dates */}
              <div className="flex justify-center items-center w-full my-2 relative">
                {/* Hindi numeral on left */}
                <span className="absolute left-0.5 sm:left-1 text-blue-600 text-[10px] sm:text-xs font-semibold flex flex-col items-center leading-none">
                  <span>{toHindiNumeral(dayNum)}</span>
                </span>
                
                {/* Big English Date */}
                <span className="text-2xl sm:text-4xl font-extrabold text-stone-900 leading-none tracking-tighter">
                  {dayNum}
                </span>

                {/* Tithi number box on right */}
                <span className="absolute right-0.5 sm:right-1 border border-red-300 text-red-600 text-[9px] sm:text-[10px] px-1 py-0.5 font-bold bg-white/50">
                  {toHindiNumeral(panchang.tithi.number)}
                </span>
              </div>

              {/* Bottom Details */}
              <div className="flex flex-col items-center justify-center w-full text-[9px] sm:text-[10px] text-stone-700 leading-tight space-y-0.5">
                <div className="flex items-center space-x-2 text-amber-700">
                  <span className="flex items-center"><Sun className="w-2.5 h-2.5 mr-0.5" />{panchang.timings.sunrise.replace(/ AM| PM/, '')}</span>
                  <span className="flex items-center"><Sun className="w-2.5 h-2.5 mr-0.5" />{panchang.timings.sunset.replace(/ AM| PM/, '')}</span>
                </div>
                {/* Nakshatra */}
                <div className="flex items-center text-stone-800 font-medium whitespace-nowrap">
                  ☆ {panchang.nakshatra.name.hi.split(' ')[0]}
                </div>
              </div>

              {/* Bottom Right Month Indicator */}
              <div className="absolute bottom-1 right-1.5 text-[9px] sm:text-[10px] text-orange-500 font-bold whitespace-nowrap">
                {panchang.hinduMonth.hi.substring(0, 2)} {toHindiNumeral(dayNum)}
              </div>
            </button>
          );
        })}
      </div>

      {/* Legend Indicators */}
      <div className="mt-4 pt-3 border-t border-stone-100 flex flex-wrap gap-4 text-xs font-semibold text-stone-500 justify-center">
        <div className="flex items-center space-x-1.5">
          <span className="w-3.5 h-3.5 rounded bg-amber-50 border border-amber-100 inline-block"></span>
          <span>शुक्ल पक्ष (Waxing)</span>
        </div>
        <div className="flex items-center space-x-1.5">
          <span className="w-3.5 h-3.5 rounded bg-indigo-50 border border-indigo-100 inline-block"></span>
          <span>कृष्ण पक्ष (Waning)</span>
        </div>
        <div className="flex items-center space-x-1.5">
          <span className="w-3.5 h-3.5 rounded bg-gradient-to-br from-saffron-50 to-orange-100 border border-saffron-400 inline-block"></span>
          <span>चयनित तिथि (Selected)</span>
        </div>
      </div>
    </div>
  );
};

export default MonthlyGrid;
